import { mergeWith, cloneDeep } from "lodash-es";
import type { Subset } from "./Type";

/**
 * Creates an object composed of the `object` properties `predicate` returns
 * truthy for. The predicate is invoked with two arguments: (value, key).
 *
 * @param object The source object
 * @param check The function to check every property
 */
export function pickBy<T extends object>(
  object: T,
  check: (item: T[keyof T], key: string) => boolean
) {
  const newObject: { [key: string]: T[keyof T] } = {};
  if (Array.isArray(object)) {
    for (let i = 0; i < object.length; i++) {
      if (check(object[i], `${i}`)) {
        newObject[i] = object[i];
      }
    }
  } else {
    for (const prop in object) {
      if (check(object[prop], prop)) {
        newObject[prop] = object[prop];
      }
    }
  }
  return newObject;
}

export function filter<T extends object>(
  object: T,
  check: (item: T[keyof T], key: string) => boolean
) {
  const newObject: Array<T[keyof T]> = [];
  for (const prop in object) {
    if (check(object[prop], prop)) {
      newObject.push(object[prop]);
    }
  }
  return newObject;
}

export function mapValues<
  T extends { [key: string]: unknown } | { [key: number]: unknown },
  R
>(object: T, transform: (item: T[keyof T], key: string) => R) {
  const newObject: { [key: string]: R } = {};
  for (const prop in object) {
    newObject[prop] = transform(object[prop], prop);
  }
  return newObject;
}

/**
 * Create a new object.
 */
export function defaultsDeepPreserveArrays<T>(newItem: Subset<T>, defaults: T) {
  const output: Subset<T> = {};
  [defaults, newItem].forEach((item) => {
    mergeWith(output, item, (objectValue, sourceValue) => {
      // copy arrays:
      return Array.isArray(sourceValue) ? cloneDeep(sourceValue) : undefined;
    });
  });
  return output as T;
}

/**
 * Assign values recursively.
 */
export function assignDeep<T extends object>(
  base: Subset<T>,
  ...others: Subset<T>[]
) {
  for (const other of others) {
    for (const prop in other) {
      const baseVal = base[prop];
      const otherVal = other[prop];
      if (Array.isArray(otherVal)) {
        base[prop] = otherVal as Subset<T>[Extract<keyof T, string>];
      } else if (
        ["boolean", "string", "number", "bigint", "function"].includes(
          typeof otherVal
        )
      ) {
        base[prop] = otherVal;
      } else if (baseVal == null || otherVal == null) {
        base[prop] = otherVal;
      } else if (typeof baseVal === "object" && typeof otherVal === "object") {
        assignDeep(baseVal, otherVal);
      } else {
        base[prop] = otherVal;
      }
    }
  }
  return base;
}

/**
 * Deep clone `source` into `target`.
 * @param target 
 * @param source 
 * @returns 
 */
export function cloneInto<T extends object>(target: T, source: Subset<T>) {
  for (const key in source) {
    const value = source[key];
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        target[key] = [...cloneDeep(value)] as T[Extract<keyof T, string>];
      } else {
        if (typeof target[key] !== "object" || target[key] === null) {
          target[key] = {} as T[Extract<keyof T, string>];
        }
        cloneInto(target[key] as T, value);
      }
    } else {
      target[key] = value as T[Extract<keyof T, string>];
    }
  }
  return target;
}

export function transformKeys<T extends { [k: string]: V }, V>(
  keyTransform: (key: string) => string,
  object: unknown
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  if (typeof object == "boolean") return object as boolean;
  if (typeof object == "undefined") return undefined;
  if (typeof object == "number") return object as number;
  if (object == null) return null;
  if (typeof object == "string") return object as string;

  if (Array.isArray(object)) {
    return object.map((v) => transformKeys(keyTransform, v));
  }

  const newObject: {
    [prop in keyof T]?: T[prop];
  } = {};

  for (const key in object) {
    const newK = keyTransform(key);
    const value = transformKeys(keyTransform, object[key as keyof object]);
    newObject[newK as keyof typeof newObject] = value;
  }

  return newObject;
}

/**
 * Returns a property of a group of item (fetched by a getter)
 * if the property has the SAME value in all of the items.
 * Otherwise, returns the default value.
 */
export function commonOrDefault<V, T>(
  collection: Array<T>,
  getter: (item: T, i: number) => V,
  defaultValue: V
) {
  if (collection.length == 0) return defaultValue;

  const value = getter(collection[0], 0);

  for (let i = 1; i < collection.length; i++) {
    const itemValue = getter(collection[i], i);
    if (itemValue != value) return defaultValue;
  }

  return value;
}
